import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Brand } from '../components/PortalShell';
import { IDENTITY_APPLICATION_ID } from '../config/identity';

const encodePassword = (password) => {
  const utf8 = encodeURIComponent(password).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)));
  return btoa(utf8);
};

const PortalSignIn = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [values, setValues] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [applicationDenied, setApplicationDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (auth.status === 'authorized') navigate('/admin/users', { replace: true });
    if (auth.status === 'denied') navigate('/access-denied', { replace: true });
  }, [auth.status, navigate]);

  if (location.search.includes('idt=') && location.search.includes('at=')) return <Navigate replace to={`/success${location.search}`} />;

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setApplicationDenied(false);
    if (!values.email.trim() || !values.password) {
      setError('Enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      await auth.signIn({ email: values.email.trim(), passwd: encodePassword(values.password), applicationId: IDENTITY_APPLICATION_ID });
    } catch (requestError) {
      const denied = requestError.details?.error?.code === 'application_not_allowed';
      setApplicationDenied(denied);
      setError(denied ? 'Contact your administrator to request access.' : requestError.message || 'Sign-in failed. Check your details and try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <main className="signin-page">
      <section className="signin-intro" aria-labelledby="signin-title">
        <Brand />
        <div className="signin-thesis">
          <p className="eyebrow">One identity. Clear access.</p>
          <h1 id="signin-title">The administrative home for every DigitalWerks user.</h1>
          <p>Review identities, shape application access, and keep a reliable audit trail from one focused workspace.</p>
        </div>
        <div className="identity-lines" aria-hidden="true"><span /><span /><span /></div>
      </section>
      <section className="signin-panel" aria-labelledby="form-title">
        <div className="signin-form-wrap">
          <p className="eyebrow">Authorized staff</p>
          <h2 id="form-title">Sign in to administration</h2>
          <p>Any DigitalWerks identity can authenticate. Administration requires access level 10 or higher.</p>
          <form onSubmit={submit} noValidate>
            <label htmlFor="email">Email address</label>
            <input id="email" name="email" type="email" autoComplete="email" autoFocus required value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })} aria-describedby={error ? 'signin-error' : undefined} />
            <label htmlFor="password">Password</label>
            <input id="password" name="password" type="password" autoComplete="current-password" required value={values.password} onChange={(e) => setValues({ ...values, password: e.target.value })} aria-describedby={error ? 'signin-error' : undefined} />
            {error && <div id="signin-error" className={`form-error${applicationDenied ? ' application-denied' : ''}`} role="alert">{applicationDenied && <strong>Application not allowed</strong>}<span>{error}</span></div>}
            <button className="button button--primary button--full" disabled={submitting} type="submit">{submitting ? 'Signing in…' : 'Sign in'}</button>
          </form>
          <a className="text-link" href="/password-reset/91bc3fdd-f9b3-4262-9b3f-169e645d12ed">Forgot your password?</a>
        </div>
      </section>
    </main>
  );
};

export default PortalSignIn;
